import type { BrandCapability, BrandDashboardPlan, CapabilityMap } from './auth.types'

// Maps each FE semantic capability (BrandCapability) to the wire key the brand
// API uses in GET /me's `capabilities` map. Today the BE map is identical to our
// names, but the authoritative symbol names land in a later slice and may differ.
//
// ⚠️ When the BE finalizes the capability symbols, this is the ONLY place to edit
// — screens keep gating on the stable BrandCapability names.
const BE_CAPABILITY_KEYS: Record<BrandCapability, string> = {
  dashboard: 'dashboard',
  creator: 'creator',
  performance_page: 'performance_page',
  named_creator_metrics: 'named_creator_metrics',
  request_placements: 'request_placements',
  book_placements: 'book_placements',
  subscription: 'subscription',
  company_shops: 'company_shops',
}

// Projects the raw /me capabilities map (keyed by BE wire strings) onto the FE
// CapabilityMap (keyed by BrandCapability). Unknown BE keys are ignored.
export function normalizeCapabilities(raw: Record<string, boolean>): CapabilityMap {
  const out: CapabilityMap = {}
  for (const capability of Object.keys(BE_CAPABILITY_KEYS) as BrandCapability[]) {
    if (raw[BE_CAPABILITY_KEYS[capability]]) {
      out[capability] = true
    }
  }
  return out
}

// TEMPORARY plan→capability bridge for capabilities the BE has NOT shipped yet —
// now just the Placements ones (request_placements / book_placements). The KPI
// capabilities (dashboard / creator / performance_page / named_creator_metrics)
// are now delivered by /me (PR #1934) and must NOT be bridged, so plan and env
// stay the single source of truth for them. Real BE capabilities overlay this in
// meToBrandUser. `subscription` is a real BE capability, owned by /me.
// NOTE: /me returns the plan again (nested under `company`), so this bridge is
// ACTIVE — it supplies the Placements capabilities the BE still omits from the
// map. Remove entirely once /me returns the Placements capabilities too.
export function capabilitiesForPlan(plan?: BrandDashboardPlan): CapabilityMap {
  switch (plan) {
    case 'expert':
    case 'pro':
      return {
        request_placements: true,
        book_placements: true,
      }
    case 'basic':
      // Basic sees Placements but not the booking section.
      return {
        request_placements: true,
      }
    default:
      // off / paused / undefined → no capabilities.
      return {}
  }
}
